import * as Sentry from '@sentry/react';
import { getNativeAppInfo } from '@workspace/global-utils';
import { Config } from '@/utils/config';
import { sentryTracePropagationTargets, shouldCreateSpanForRequest } from './trace-filters';

const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_DEPLOYED_ENV ?? import.meta.env.MODE;
const release = import.meta.env.VITE_SENTRY_RELEASE;

// Any non-dev build with a DSN reports. Gating on `environment === 'production'`
// silently no-ops captureException when the server .env is staging/unset.
export const isSentryEnabled =
  Boolean(dsn) && import.meta.env.VITE_DISABLE_SENTRY !== 'true' && !import.meta.env.DEV;

const MINIFIED_REACT_ERROR = /Minified React error #(\d+)/;

const shouldIgnoreSentryEvent = (event: Sentry.ErrorEvent) => {
  const error = event.exception?.values?.[0];
  const errorMessage = error?.value;
  const errorType = error?.type;
  const frames = error?.stacktrace?.frames ?? [];
  const filenames = frames.map((frame) => frame.filename ?? frame.abs_path ?? '');

  if (errorType === 'TRPCClientError') return true;

  // Headless scrapers often block stylesheets, which fails Vite's CSS preload for lazy routes.
  // Kept narrow: our PDF exporter is also HeadlessChrome and its other errors must still report.
  if (
    navigator.userAgent.includes('HeadlessChrome') &&
    event.exception?.values?.some((value) => value.value?.startsWith('Unable to preload CSS'))
  ) {
    return true;
  }

  if (errorMessage && /Object Not Found Matching Id:\d+, MethodName:update/i.test(errorMessage)) {
    return true;
  }

  if (
    filenames.length > 0 &&
    filenames.every(
      (filename) =>
        /widget-[A-Za-z0-9_-]+\.js/i.test(filename) ||
        /script\.esm\/embedded/i.test(filename) ||
        /chrome-extension:/i.test(filename) ||
        /moz-extension:/i.test(filename),
    )
  ) {
    return true;
  }

  return (
    event.tags?.source === 'builder-save' &&
    errorMessage === 'User does not have access to this proposal'
  );
};

export function initSentry() {
  if (!isSentryEnabled) {
    return;
  }

  const nativeApp = getNativeAppInfo();

  Sentry.init({
    dsn,
    environment,
    ...(release ? { release } : {}),
    ...(nativeApp
      ? {
          initialScope: {
            tags: {
              app_platform: nativeApp.platform,
              app_version: nativeApp.version,
              app_build: nativeApp.build ?? undefined,
            },
          },
        }
      : {}),
    sendDefaultPii: true,
    attachStacktrace: true,
    integrations: [
      Sentry.browserTracingIntegration({
        enableReportPageLoaded: true,
        enableLongTask: true,
        enableLongAnimationFrame: true,
        enableInp: true,
        shouldCreateSpanForRequest,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: sentryTracePropagationTargets(),
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      if (shouldIgnoreSentryEvent(event)) {
        return null;
      }

      const message = event.exception?.values?.[0]?.value;
      const reactCode = message?.match(MINIFIED_REACT_ERROR)?.[1];
      if (reactCode) {
        event.tags = { ...event.tags, react_error_code: reactCode };
      }

      const componentStack = event.extra?.componentStack;
      if (
        typeof componentStack === 'string' &&
        componentStack.length > Config.SENTRY.COMPONENT_STACK_MAX_LENGTH
      ) {
        event.extra = {
          ...event.extra,
          componentStack: `${componentStack.slice(0, Config.SENTRY.COMPONENT_STACK_MAX_LENGTH)}\n…truncated`,
        };
      }

      return event;
    },
    ignoreErrors: [
      /ResizeObserver loop/,
      // Exact network failures only — substring 'Failed to fetch' also dropped
      // "Failed to fetch dynamically imported module", which blanks the editor.
      /^Failed to fetch$/,
      'NetworkError when attempting to fetch resource',
      /^Load failed$/,
      'The network connection was lost',
      'The Internet connection appears to be offline',
      'Network request failed',
      /^AbortError$/,
      'The user aborted a request',
    ],
    denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /script\.esm\/embedded/i],
  });
}
